import {useState} from 'react';
import { useMutation } from '@tanstack/react-query';
import { lookupPayoutRecipient, payThroughSendMoney } from '../../../../services/AccountService';
import {toast} from 'react-toastify';
import {formatKenyanPhoneNumber} from "../../../../utils/helpers";
import PayoutRecipientConfirmationModal from './PayoutRecipientConfirmationModal';
import ExpenseTagsPicker from './ExpenseTagsPicker';
import { readErrorEnvelope } from '../../../../utils/errorEnvelope';
import { SEND_MONEY_MINIMUM_AMOUNT } from '../../../../utils/payoutConstants';
import PinInput from '../../PinInput';

const SendMoneyForm = ({
                         expenseTags,
                         expenseTagsLoading,
                         userWalletBalance,
                         userId,
                         userWalletBalanceLoading,
                         onTransactionSubmitted,
                       }) => {

  const [selectedTags, setSelectedTags] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pinAttemptsRemaining, setPinAttemptsRemaining] = useState(null);
  const [pinLockRetryAfterSeconds, setPinLockRetryAfterSeconds] = useState(null);
  const [formData, setFormData] = useState({
    sendMoneyRecipientPhoneNumber: '',
    sendMoneyAmount: '',
    transactionPin: '',
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [verifiedRecipientName, setVerifiedRecipientName] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);

  const payThroughSendMoneyMutation = useMutation({
    mutationFn: (payload) => payThroughSendMoney(userId, payload),
  });

  const handleInputChange = (e) => {
    const {id, value} = e.target;
    let finalValue;

    if (id === "sendMoneyRecipientPhoneNumber") {
      finalValue = formatKenyanPhoneNumber(value);
    } else {
      finalValue = value;
    }
    setFormData((prev) => ({...prev, [id]: finalValue}));
  };

  const handlePayViaSendMoney = async (e) => {
    e.preventDefault();

    if (paymentProcessing) return;
    setPaymentProcessing(true);

    const selectedTagIds = (expenseTags || [])
      .filter(tag => selectedTags.includes(tag.name))
      .map(tag => tag.id);

    const payload = {
      PartyB: formData.sendMoneyRecipientPhoneNumber,
      Amount: formData.sendMoneyAmount,
      expense_tag_ids: selectedTagIds,
      transaction_pin: formData.transactionPin,
    };

    setPendingPayload(payload);

    try {
      const response = await lookupPayoutRecipient(userId, 'send-money', formData.sendMoneyRecipientPhoneNumber);
      setVerifiedRecipientName(response.data.receiver_name);
    } catch {
      setVerifiedRecipientName(null);
    }

    setShowConfirmationModal(true);
  };

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
    setPaymentProcessing(false);
    setPendingPayload(null);
    setVerifiedRecipientName(null);
  };

  const handleModalConfirm = async () => {
    try {
      await toast.promise(
        payThroughSendMoneyMutation.mutateAsync(pendingPayload),
        {
          pending: 'Processing request ...',
          success: 'Your request will be processed shortly.',
          error: {
            render({ data: err }) {
              return readErrorEnvelope(err).message;
            }
          }
        }
      );
      onTransactionSubmitted();
      resetForm();
    } catch (err) {
      const { errorCode, meta } = readErrorEnvelope(err);
      if (errorCode === 'PIN_INVALID') {
        setPinAttemptsRemaining(meta.remaining_attempts ?? null);
        setPinLockRetryAfterSeconds(null);
      } else if (errorCode === 'PIN_LOCKED') {
        setPinLockRetryAfterSeconds(meta.retry_after_seconds ?? null);
        setPinAttemptsRemaining(null);
      }
    } finally {
      setShowConfirmationModal(false);
      setPaymentProcessing(false);
      setPendingPayload(null);
      setVerifiedRecipientName(null);
    }
  };

  const resetForm = () => {
    setFormData({
      sendMoneyRecipientPhoneNumber: '',
      sendMoneyAmount: '',
      transactionPin: '',
    });
    setSelectedTags([]);
    setPinAttemptsRemaining(null);
    setPinLockRetryAfterSeconds(null);
  }

  const isAmountBelowMinimum = formData.sendMoneyAmount !== '' && Number(formData.sendMoneyAmount) < SEND_MONEY_MINIMUM_AMOUNT;

  const areAllFieldsFilled = () => {
    return formData.sendMoneyRecipientPhoneNumber
      && formData.sendMoneyAmount
      && Number(formData.sendMoneyAmount) >= SEND_MONEY_MINIMUM_AMOUNT
      && selectedTags.length > 0;
  }

  return (
    <>
      <form onSubmit={handlePayViaSendMoney}>
        <div className="sell-blance">
          <div className="form-label blance"><span>BALANCE:</span>
            {userWalletBalanceLoading ? (
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status"/>
            ) : (
              <p>{userWalletBalance}</p>
            )}
          </div>
          <label className="form-label text-primary" htmlFor='sendMoneyRecipientPhoneNumber'>Phone Number</label>

          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder=""
              id='sendMoneyRecipientPhoneNumber'
              value={formData.sendMoneyRecipientPhoneNumber}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="sell-blance">
          <label className="form-label text-primary" htmlFor='sendMoneyAmount'>Amount</label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              placeholder=""
              id='sendMoneyAmount'
              min={SEND_MONEY_MINIMUM_AMOUNT}
              step="1"
              value={formData.sendMoneyAmount}
              onChange={handleInputChange}
            />
          </div>
          {isAmountBelowMinimum && (
            <small className="text-danger mt-1 d-block">Minimum amount is KES {SEND_MONEY_MINIMUM_AMOUNT}.</small>
          )}
        </div>
        <div className="sell-blance">
          {expenseTagsLoading ? (
            <>
              <label className="form-label text-muted small">Expense Tag (select many):</label>
              <span className="spinner-border spinner-border-sm text-primary ms-2" role="status"/>
            </>
          ) : (
            <ExpenseTagsPicker
              tags={expenseTags}
              selectedNames={selectedTags}
              onChange={setSelectedTags}
            />
          )}
        </div>
        {/* <div className="sell-blance">
          <label className="form-label text-primary" htmlFor='sendMoneyExpenseDescription'>Expense Description</label>
          <div className="input-group">
            <textarea
              className="form-txtarea form-control"
              rows="4"
              id="sendMoneyExpenseDescription"
              value={formData.sendMoneyExpenseDescription}
              onChange={handleInputChange}
            ></textarea>
          </div>
        </div> */}

        {areAllFieldsFilled() &&
          (
            <div className="sell-blance mt-3">
              <label className="form-label text-danger" htmlFor="transactionPin">
                Enter Transaction PIN
              </label>
              <div className="input-group">
                <PinInput
                  placeholder="Enter your PIN"
                  id="transactionPin"
                  name="transactionPin"
                  value={formData.transactionPin}
                  onChange={handleInputChange}
                />
              </div>
              {pinAttemptsRemaining !== null && (
                <small className={`mt-1 d-block ${pinAttemptsRemaining <= 1 ? 'text-danger' : 'text-warning'}`}>
                  {pinAttemptsRemaining === 0
                    ? 'No attempts remaining.'
                    : `${pinAttemptsRemaining} attempt${pinAttemptsRemaining === 1 ? '' : 's'} remaining.`}
                </small>
              )}
              {pinLockRetryAfterSeconds !== null && (
                <small className="mt-1 d-block text-danger">
                  Your PIN is locked. Try again in {Math.ceil(pinLockRetryAfterSeconds / 60)} minute{Math.ceil(pinLockRetryAfterSeconds / 60) === 1 ? '' : 's'}.
                </small>
              )}
            </div>
          )}
        <div className="text-center">
          <button
            className="btn btn-primary w-75"
            disabled={
              !formData.sendMoneyRecipientPhoneNumber ||
              !formData.sendMoneyAmount ||
              Number(formData.sendMoneyAmount) < SEND_MONEY_MINIMUM_AMOUNT ||
              selectedTags.length === 0 ||
              !formData.transactionPin ||
              paymentProcessing
            }
          >
            {paymentProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing
              </>
            ) : 'Pay'}
          </button>
        </div>

      </form>

      <PayoutRecipientConfirmationModal
        isOpen={showConfirmationModal}
        recipientName={verifiedRecipientName}
        destination={formData.sendMoneyRecipientPhoneNumber}
        amount={formData.sendMoneyAmount}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </>
  )
}
export default SendMoneyForm;
